import React, { useEffect, useRef, useState } from 'react';
import { useElevate } from '../ElevateContext';
import { FlyoutTarget, FlyoutTrigger } from '@jetshop/ui/Modal/Flyout';
import { ElevateAutocompleteResult } from './ElevateAutocompleteResult';
import { styled } from 'linaria/react';
import { ReactComponent as SearchIcon } from '../../../svg/fa/search.svg';
import { useIntl } from '@jetshop/intl';
import { theme } from '../../Theme';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import { useHistory, useLocation } from 'react-router';
import { useTracking } from '../../../utils/tracking/useTracking';

const ElevateSearchWrapper = styled('div')`
  padding: 10px;

  .elevate-search-input-wrapper {
    margin: 0 auto;
    position: relative;
    max-width: 820px;
    width: 100%;

    .icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 20px;
      width: 20px;
      z-index: 2;
      cursor: pointer;
      transition: opacity 0.2s ease;

      ${theme.below.md} {
        left: 12px;
      }

      &:hover {
        opacity: 0.7;
      }
    }

    input {
      height: 38px;
      color: rgb(0, 0, 0);
      width: 100%;
      font-size: 16px;
      padding-left: 65px;
      padding-right: 10px;
      appearance: none;
      background: transparent;
      border-width: 1px;
      border-style: solid;
      border-color: rgb(210, 210, 210);
      border-image: initial;
      margin: auto;
      anchor-name:--elevate-search-input;

      ${theme.below.md} {
        padding-left: 46px;
        padding-right: 0px;
      }

      ::placeholder {
        color:rgb(70, 70, 70);
      }
      &:focus {
        outline: none;
        border-color: #80937b;
        box-shadow: 0 0 0 1px rgba(255, 102, 0, 0.1);
      }

      -webkit-appearance: none;
      ::-webkit-search-cancel-button {
        -webkit-appearance: none;
        content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath d='M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z' fill='currentColor'/%3E%3C/svg%3E");
        height: 14px;
        width: 14px;
        display: block;
        cursor: pointer;
        margin-right: 16px; /* Add some margin to position it properly */
      }
    }
  }
`;

const ElevateSearchFlyout = ({
  searchData,
  setSearchData,
  defaultResults,
  searchResults,
  handleSearchTermChangedCallback,
  fetchDefaultResults,
  placeholderString,
  history,
  routes
}) => {
  const wrapperRef = useRef(null);

  return (
    <FlyoutTrigger id="elevate-autocomplete-result">
      {({ showTarget, isOpen, hideTarget }) => (
        <ElevateSearchFlyoutContent
          wrapperRef={wrapperRef}
          showTarget={showTarget}
          isOpen={isOpen}
          hideTarget={hideTarget}
          searchData={searchData}
          setSearchData={setSearchData}
          defaultResults={defaultResults}
          searchResults={searchResults}
          handleSearchTermChangedCallback={handleSearchTermChangedCallback}
          fetchDefaultResults={fetchDefaultResults}
          placeholderString={placeholderString}
          history={history}
          routes={routes}
        />
      )}
    </FlyoutTrigger>
  );
};

const ElevateSearchFlyoutContent = ({
  wrapperRef,
  showTarget,
  isOpen,
  hideTarget,
  searchData,
  setSearchData,
  defaultResults,
  searchResults,
  handleSearchTermChangedCallback,
  fetchDefaultResults,
  placeholderString,
  history,
  routes
}) => {
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = event => {
      // Keep open only when clicking the actual input or results panel.
      // Clicking the wrapper/background area (e.g. around the input in the header)
      // should close the flyout.
      if (inputRef.current?.contains(event.target)) return;
      if (resultsRef.current?.contains(event.target)) return;

      hideTarget?.();
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, hideTarget, wrapperRef]);

  const handleOnFocus = () => {
    showTarget?.();

    if (defaultResults) return;

    fetchDefaultResults();
  };

  return (
    <ElevateSearchWrapper ref={wrapperRef} className="elevate-search-wrapper">
      <div className="elevate-search-input-wrapper">
        <SearchIcon className="icon" />
        <input
          id="elevate-autocomplete-input"
          ref={inputRef}
          type="search"
          name="elevate-search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          value={searchData?.term}
          onFocus={handleOnFocus}
          onChange={e => {
            setSearchData({ term: e.target.value, params: {} });
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (searchData?.term) {
              history?.push(
                `${routes?.search?.path}?term=${encodeURIComponent(
                    searchData?.term
                  )}`
                );
                hideTarget?.();
              }
            }
          }}
          placeholder={placeholderString}
        />
      </div>
      <FlyoutTarget id="elevate-autocomplete-result">
        {() => (
          <>
            {isOpen && (
              <div ref={resultsRef}>
                <ElevateAutocompleteResult
                  results={searchResults || defaultResults}
                  searchTermChangedCallback={handleSearchTermChangedCallback}
                  hideTarget={hideTarget}
                  isDefaultData={!searchResults}
                  searchTerm={searchData?.term}
                />
              </div>
            )}
          </>
        )}
      </FlyoutTarget>
    </ElevateSearchWrapper>
  );
};

export const ElevateSearchInput = () => {
  const history = useHistory();
  const location = useLocation();
  const { routes } = useShopConfig();
  const { trackSearch } = useTracking();
  const { getAutoCompleteData } = useElevate();
  const [defaultResults, setDefaultResults] = useState(null);
  const [searchData, setSearchData] = useState({ term: '', params: {} });
  const [searchResults, setSearchResults] = useState(null);
  const mountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const t = useIntl();
  const placeholderString = t(
    'Search for products for horse, riders and dogs...'
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setSearchData({ term: '', params: {} });
  }, [location.pathname]);

  useEffect(() => {
    const term = searchData?.term?.trim?.() ?? '';
    const params = searchData?.params ?? {};
    if (!term) {
      requestIdRef.current += 1;
      setSearchResults(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      try {
        const results = await getAutoCompleteData({
          searchTerm: term,
          ...params
        });
        if (!mountedRef.current) return;
        if (requestId !== requestIdRef.current) return;
        trackSearch({
          searchTerm: term,
          results: results?.totalHits ?? 0,
          placement: 'Quick search'
        });
        setSearchResults(results ?? null);
      } catch (e) {
        if (!mountedRef.current) return;
        if (requestId !== requestIdRef.current) return;
        setSearchResults(null);
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [searchData, getAutoCompleteData]);

  const fetchDefaultResults = async () => {
    const results = await getAutoCompleteData({ searchTerm: '' });
    setDefaultResults(results ?? null);
  };

  const handleSearchTermChangedCallback = (term, params) => {
    setSearchData({ term, params });
  };

  return (
    <ElevateSearchFlyout
      searchData={searchData}
      setSearchData={setSearchData}
      defaultResults={defaultResults}
      searchResults={searchResults}
      handleSearchTermChangedCallback={handleSearchTermChangedCallback}
      fetchDefaultResults={fetchDefaultResults}
      placeholderString={placeholderString}
      history={history}
      routes={routes}
    />
  );
};
